// packages/core/src/learning/schools/buffett-munger.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'warren-buffett',
// 'charlie-munger', 'benjamin-graham' (all valid FigureIds) — wire into
// `figures` during the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const BUFFETT_MUNGER_EXHIBIT: SchoolExhibitEntry = {
	id: 'buffett-munger',
	tagline:
		'Buffett and Munger believe great decisions beat constant activity.',
	worldviewSummary:
		'Buffett and Munger investing is paying attention to business quality, temperament, incentives, patience, and avoiding obvious stupidity. This lens protects you from treating investing like entertainment, constant action, or clever prediction. It accepts that real skill is rare, waiting is uncomfortable, and most opportunities are not worth touching. The blind spot is that this worldview can make people overestimate their ability to judge businesses, especially when they confuse a good story with a good price.',
	coreBeliefs: [
		'Buffett and Munger believe patience is a competitive advantage.',
		'Buffett and Munger believe a great business at a fair price can matter more than a cheap business with weak economics.',
		'Buffett and Munger believe temperament matters because panic and ego can ruin good analysis.',
		'Buffett and Munger believe incentives, management behavior, and business quality are not side details.',
		'Buffett and Munger believe avoiding dumb mistakes is often more powerful than chasing brilliant moves.',
		'Buffett and Munger believe you should know the edge of your own understanding.',
	],
	warnsAgainst: [
		'Treating investing like constant motion instead of careful selection.',
		'Buying a cheap looking asset without understanding why it is cheap.',
		'Confusing confidence, charisma, or a clean story with real business quality.',
		'Thinking patience is easy when markets, peers, and headlines are pressuring you to act.',
	],
	helps: 'This lens helps people who want to understand investing through business quality, patience, incentives, and temperament. It may not fit people who want broad simplicity first, or anyone tempted to turn a few famous principles into overconfident stock selection.',
	figures: [],
	canon: [
		{
			title: 'The Intelligent Investor',
			author: 'Benjamin Graham',
			oneLineIdea:
				'Price and value are not the same thing, and a margin of safety protects you from being too sure.',
		},
		{
			title: 'Berkshire Hathaway shareholder letters',
			author: 'Warren Buffett',
			oneLineIdea:
				'Long term business ownership requires patience, discipline, and honest thinking about quality.',
		},
		{
			title: 'Poor Charlie’s Almanack',
			author: 'Charlie Munger',
			oneLineIdea:
				'Better decisions come from mental models, incentives, inversion, and avoiding obvious mistakes.',
		},
		{
			title: 'Circle of competence',
			oneLineIdea:
				'You do not need to understand everything, but you do need to know what you do not understand.',
		},
		{
			title: 'Moats and durable advantage',
			oneLineIdea:
				'A strong business needs reasons its economics can survive competition and time.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-buffett-munger',
	debateIds: [],
	applyActions: [
		{
			label: 'Compare patient ownership against constant tinkering',
			action: {
				type: 'openSimulator',
				label: 'Simulate patient behavior',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check whether your money picture can handle waiting',
			action: { type: 'openDashboard', label: 'Open your Dashboard' },
		},
		{
			label: 'Build a plan that does not depend on constant action',
			action: { type: 'buildFreedomPlan', label: 'Build a patient plan' },
		},
		{
			label: 'Write the edge of what you truly understand',
			action: {
				type: 'noToolAction',
				label: 'Define your circle',
				prompt: 'Write three areas you understand well, and three areas where you are most likely to fool yourself.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Buffett and Munger investing is not about action. It is about judgment.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of investing entertainment is that it feels productive while weakening discipline.',
			kind: 'hook',
		},
		{
			line: 'A cheap price can still be expensive if the business is weak.',
			kind: 'hook',
		},
		{
			line: 'What investing mistake taught you that temperament matters more than intelligence?',
			kind: 'interview-question',
		},
		{
			line: 'Where do people misunderstand Buffett and Munger the most?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps users compare investing worldviews without pretending one lens fits every person.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Most investing content rewards action. Nisk teaches users to examine the tradeoff before acting.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show someone a tempting investment story, then ask what the business actually has to prove.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask investors what they avoided, not just what they bought.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
